import {
  Autocomplete,
  AutocompleteValue,
  Box,
  MenuList,
  styled,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import useCreation from "ahooks/es/useCreation";
import useMemoizedFn from "ahooks/es/useMemoizedFn";
import React from "react";
import { AlertCircle, Bell } from "react-feather";
import { useTranslation } from "react-i18next";
import {
  MemberChip,
  MemberMenuItemContent,
} from "../../../../global/components";
import {
  StyledListSubheader,
  StyledPopper,
  StyledTeamName,
} from "../../../../global/styles";
import { stripDiacritics } from "../../../assets/helperFunctions";
import { useOrganizationMembership } from "../../../authentication/hooks";
import { GroupMember } from "../../domain";
import { useAutocompleteOptions, useBookingGroups } from "../../hooks";
import { useBookingForm, useSelection } from "../../pages/BookingPage/state";
import { createFuse } from "./search";

const MIN_CHARS_TO_SEARCH = 2;
const CHUNK_SIZE = 50;

export type BookForAutocompleteProps = Omit<
  TextFieldProps,
  "value" | "onChange"
> & {
  multiple: boolean;
  capacityReached: boolean;
  value: GroupMember[];
  onChange?: (member: GroupMember[]) => void;
};

export const Icon = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexBasis: "24px",
  width: "24px",
}));

const CapacityErrorMessage: React.FC = () => {
  const { t } = useTranslation("booking");
  return (
    <Box
      component="li"
      role="presentation"
      sx={(theme) => ({
        position: "fixed",
        display: "flex",
        bottom: theme.spacing(1),
        left: theme.spacing(1),
        right: theme.spacing(1),
        backgroundColor: "errors.light",
        borderRadius: "8px",
        paddingX: 1,
        paddingY: 0.5,
      })}
    >
      <Icon>
        <AlertCircle size={16} />
      </Icon>
      <Typography variant="body3" color="shades.dark">
        {t("form.feedback.capacity-reached")}
      </Typography>
    </Box>
  );
};

const DedicatedHeader: React.FC<{ hasSelected: boolean }> = ({
  hasSelected,
}) => {
  const { t } = useTranslation("booking");
  const { selectedDesk } = useSelection();

  return (
    <Box
      component="li"
      role="presentation"
      sx={{
        display: "flex",
        backgroundColor: "orange.light",
        borderRadius: "8px",
        px: 1,
        py: 0.5,
        width: "100%",
      }}
    >
      <Icon>
        <Bell size={16} />
      </Icon>
      <Typography variant="body3" color="shades.dark">
        {selectedDesk?.dedicatedFor.length && !hasSelected
          ? t("form.feedback.dedicated-desk")
          : t("form.feedback.dedicated-room")}
      </Typography>
    </Box>
  );
};

const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & { capacityReached?: boolean }
>(function (props, ref) {
  const { children, capacityReached, ...other } = props;
  return (
    <MenuList
      component="div"
      ref={ref}
      {...other}
      sx={{ position: "relative", p: 1 }}
    >
      {children}
      {capacityReached && <CapacityErrorMessage />}
    </MenuList>
  );
});

function isOptionEqualToValue(option: GroupMember, value: GroupMember) {
  return option.id === value.id;
}

function groupByGroupName(option: GroupMember) {
  return option.groupName;
}

export const BookForAutocomplete: React.FC<BookForAutocompleteProps> = ({
  value,
  onChange,
  multiple,
  capacityReached,
  ...props
}) => {
  const { t } = useTranslation("booking");
  const { organization } = useOrganizationMembership();
  const { selectedDesk } = useSelection();
  const form = useBookingForm();
  if (!form.target) throw new Error("Invalid usage of BookForAutocomplete");

  const { isLoading } = useBookingGroups(
    organization.id,
    form.target.date,
    form.isEditing ? form.source.bookingId : null
  );

  const options: GroupMember[] = useAutocompleteOptions(value);
  const fuse = useCreation(() => createFuse(options), [options]);

  const selectedIds = React.useMemo(
    () => new Set(value.map((v) => v.id)),
    [value]
  );

  const [currentInput, setCurrentInput] = React.useState("");
  const deferredInput = React.useDeferredValue(currentInput);

  const [visibleCount, setVisibleCount] = React.useState(CHUNK_SIZE);

  React.useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [deferredInput]);

  const filteredOptionsAll = React.useMemo(() => {
    const raw = deferredInput.trim();
    if (raw.length < MIN_CHARS_TO_SEARCH) {
      return [...options].sort((a, b) => {
        const aScore =
          (a.bookableForPeriod ? 2 : 0) + (selectedIds.has(a.id) ? 1 : 0);
        const bScore =
          (b.bookableForPeriod ? 2 : 0) + (selectedIds.has(b.id) ? 1 : 0);
        return bScore - aScore;
      });
    }

    const query = stripDiacritics(deferredInput);
    const result = fuse.search(query);
    return result.map((it) => it.item);
  }, [deferredInput, options, fuse, selectedIds]);

  const visibleOptions = React.useMemo(
    () => filteredOptionsAll.slice(0, visibleCount),
    [filteredOptionsAll, visibleCount]
  );

  const handleListboxScroll = useMemoizedFn(
    (e: React.UIEvent<HTMLUListElement>) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

      if (nearBottom && visibleOptions.length < filteredOptionsAll.length) {
        setVisibleCount((prev) =>
          Math.min(prev + CHUNK_SIZE, filteredOptionsAll.length)
        );
      }
    }
  );

  const handleChange = useMemoizedFn(
    (
      event: React.SyntheticEvent,
      next: AutocompleteValue<GroupMember, any, unknown, unknown>
    ) => {
      event.stopPropagation();
      if (!Array.isArray(next)) {
        onChange?.(next ? [next] : []);
        return;
      }
      if (
        next.length === 1 &&
        selectedDesk?.isDedicated &&
        !selectedDesk.dedicatedFor.includes(next[0].user.id)
      ) {
        onChange?.([]);
      } else {
        onChange?.(next);
      }
    }
  );

  if (isLoading) return null;

  return (
    <Autocomplete
      fullWidth
      multiple={multiple}
      options={visibleOptions}
      value={multiple ? value : value[0] ?? null}
      onChange={handleChange}
      onInputChange={(_, v) => setCurrentInput(v)}
      groupBy={groupByGroupName}
      isOptionEqualToValue={isOptionEqualToValue}
      filterSelectedOptions
      disableClearable
      autoHighlight
      disableCloseOnSelect={multiple}
      filterOptions={(opts) => opts}
      PopperComponent={StyledPopper}
      ListboxComponent={ListboxComponent}
      ListboxProps={{ onScroll: handleListboxScroll, capacityReached } as any}
      getOptionLabel={(option) => option.user.name.full}
      getOptionDisabled={(option) => {
        const notSelected = !selectedIds.has(option.id);
        return notSelected && (capacityReached || !option.bookableForPeriod);
      }}
      noOptionsText={t("form.no-options")}
      renderInput={(params) => <TextField {...params} {...props} />}
      renderTags={(vals, getTagProps) =>
        vals.map((member, index) => {
          const tagProps = getTagProps({ index });
          return <MemberChip {...tagProps} label={member.user.name.full} />;
        })
      }
      renderGroup={({ group, ...gp }) => (
        <GroupHeader hasSelected={!!value.length} label={group} {...gp} />
      )}
      renderOption={(liProps, option) => (
        <MemberMenuItemContent
          elementProps={liProps}
          avatarImageUrl={option.user.avatarImageUrlSrc}
          name={option.user.name.full}
          available={option.bookableForPeriod}
          team={
            option.team ? `${t("form.feedback.team")} ${option.team}` : null
          }
          errorText={t("form.feedback.booked")}
          user={option.user}
        />
      )}
      data-testid="organization-members-autocomplete"
    />
  );
};
interface GroupHeaderProps {
  hasSelected: boolean;
  label: string;
  children?: React.ReactNode;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({
  hasSelected,
  label,
  children,
}) => {
  const { t } = useTranslation("booking");
  return (
    <div>
      <StyledListSubheader
        disableSticky
        borderBottom={label === "dedicatedGroup"}
        sx={{
          ...(label === "dedicatedGroup"
            ? {
                "&.MuiListSubheader-root": {
                  mb: 0,
                  pb: 0,
                },
              }
            : {}),
        }}
      >
        {label === "dedicatedGroup" ? (
          <DedicatedHeader hasSelected={hasSelected} />
        ) : (
          <>
            <Typography variant="body3">{t("form.feedback.team")}</Typography>
            <StyledTeamName>{label}</StyledTeamName>
          </>
        )}
      </StyledListSubheader>
      <ul style={{ margin: 0, padding: 0 }}>{children}</ul>
    </div>
  );
};
